import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ImageUpload from "@/components/ImageUpload";
import { allergenIcon } from "@/lib/allergens";
import { Plus, X, Languages, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";

const LANG_LABELS = { en: "English", fr: "Français", de: "Deutsch", es: "Español", pt: "Português", hr: "Hrvatski", hu: "Magyar", nl: "Nederlands", pl: "Polski", ro: "Română", ru: "Русский", sk: "Slovenčina", zh: "中文" };

const UNIT_PRESETS = ["all'etto", "al kg", "per 100g", "a persona", "al pezzo", "Calice", "Bottiglia"];

const DishDialog = ({ open, onClose, onSaved, catId, menuId, dish, allergens, isDrink, menus = [] }) => {
  const [form, setForm] = useState({
    name: dish?.name || "",
    description: dish?.description || "",
    price: dish?.price ?? "",
    image_path: dish?.image_path || null,
    status: dish?.status || "active",
    allergens: dish?.allergens || [],
    origin: dish?.origin || "",
    alcohol: dish?.alcohol ?? "",
    formats: dish?.formats || [],
    keep_name: dish?.keep_name || false,
    per_person: dish?.per_person ?? (dish?.portion === "per_person"),
    min_two: dish?.min_two ?? (dish?.portion === "min_2"),
  });
  const [saving, setSaving] = useState(false);
  const [activeLangs, setActiveLangs] = useState([]);
  const [trans, setTrans] = useState(dish?.translations || {});
  const [selLang, setSelLang] = useState(null);
  const [edited, setEdited] = useState(() => new Set());
  const [retLoading, setRetLoading] = useState(null);
  const [destMenu, setDestMenu] = useState(menuId);
  const [destCat, setDestCat] = useState(catId);
  const [catOptions, setCatOptions] = useState([]);

  useEffect(() => {
    api.get(`/menus/${destMenu}/categories`).then((r) => {
      const opts = r.data || [];
      setCatOptions(opts);
      setDestCat((prev) => (opts.some((c) => c.id === prev) ? prev : (opts[0]?.id || prev)));
    }).catch(() => setCatOptions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destMenu]);

  useEffect(() => {
    api.get("/restaurant").then((r) => {
      const ls = (r.data.settings?.languages || []).filter((l) => l !== "it");
      setActiveLangs(ls);
      if (ls.length) setSelLang(ls[0]);
    }).catch(() => {});
  }, []);

  const updateTrans = (lang, k, v) => {
    setTrans((prev) => ({ ...prev, [lang]: { ...(prev[lang] || {}), [k]: v } }));
    setEdited((prev) => new Set(prev).add(lang));
  };

  const retranslate = async (lang) => {
    if (!dish) return;
    setRetLoading(lang || "all");
    try {
      const res = await api.post(`/dishes/${dish.id}/retranslate`, { lang: lang || null });
      setTrans(res.data.translations || {});
      setEdited(new Set());
      toast.success(lang ? "Lingua ritradotta" : "Tutte le lingue ritradotte");
    } catch (e) { toast.error("Errore nella traduzione"); }
    finally { setRetLoading(null); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleAllergen = (code) => set("allergens", form.allergens.includes(code) ? form.allergens.filter((a) => a !== code) : [...form.allergens, code]);

  const addFormat = () => set("formats", [...form.formats, { label: "", price: "" }]);
  const addPresetFormat = (label) => set("formats", [...form.formats, { label, price: "" }]);
  const updateFormat = (i, k, v) => { const arr = form.formats.map((f, idx) => idx === i ? { ...f, [k]: v } : f); set("formats", arr); };
  const removeFormat = (i) => set("formats", form.formats.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Inserisci un nome");
    setSaving(true);
    const payload = {
      category_id: destCat, menu_id: destMenu, name: form.name, description: form.description,
      price: form.price === "" ? null : Number(form.price),
      image_path: form.image_path, status: form.status, allergens: form.allergens,
      origin: isDrink ? (form.origin || null) : null,
      alcohol: isDrink && form.alcohol !== "" ? Number(form.alcohol) : null,
      formats: form.formats.filter((f) => f.label).map((f) => ({ label: f.label, price: Number(f.price) || 0 })),
      keep_name: form.keep_name,
      per_person: form.per_person,
      min_two: form.min_two,
    };
    try {
      if (dish) await api.put(`/dishes/${dish.id}`, payload);
      else await api.post("/dishes", payload);
      if (dish && edited.size) {
        const subset = {};
        edited.forEach((l) => { subset[l] = { name: trans[l]?.name || "", description: trans[l]?.description || "" }; });
        await api.put(`/dishes/${dish.id}/translations`, { translations: subset });
      }
      toast.success("Salvato");
      onSaved();
    } catch (e) { toast.error("Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg p-0 gap-0 !flex flex-col max-h-[90vh]" data-testid="dish-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b shrink-0 text-left"><DialogTitle>{dish ? "Modifica" : "Nuovo"} {isDrink ? "drink" : "piatto"}</DialogTitle><DialogDescription>Dati del piatto: nome, descrizione, prezzo, formati/varianti, posizione, allergeni e traduzioni.</DialogDescription></DialogHeader>
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
          <ImageUpload value={form.image_path} onChange={(p) => set("image_path", p)} label="Foto" testid="dish-image" />
          <div>
            <label className="text-sm font-medium">Nome</label>
            <input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="dish-name" className="mt-1 w-full rounded-xl border border-input px-4 py-2.5 focus:outline-none focus:border-[#002FA7]" />
            <label className="flex items-center gap-2 mt-2 cursor-pointer select-none">
              <input type="checkbox" checked={form.keep_name} onChange={(e) => set("keep_name", e.target.checked)} data-testid="dish-keep-name" className="w-4 h-4 accent-[#002FA7]" />
              <span className="text-sm text-muted-foreground">Mantieni il nome originale (non tradurre in altre lingue)</span>
            </label>
          </div>
          <div>
            <label className="text-sm font-medium">Descrizione</label>
            <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} data-testid="dish-description" className="mt-1 w-full rounded-xl border border-input px-4 py-2.5 focus:outline-none focus:border-[#002FA7] resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Prezzo (€)</label>
              <input type="number" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="dish-price" placeholder={isDrink ? "opzionale" : ""} className="mt-1 w-full rounded-xl border border-input px-4 py-2.5 focus:outline-none focus:border-[#002FA7]" />
            </div>
            <div>
              <label className="text-sm font-medium">Stato</label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="mt-1" data-testid="dish-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Attivo</SelectItem>
                  <SelectItem value="sold_out">Esaurito oggi</SelectItem>
                  <SelectItem value="hidden">Nascosto</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-xl border border-border p-3 space-y-2" data-testid="dish-move">
            <p className="text-sm font-medium">Posizione</p>
            {menus.length > 1 && (
              <div>
                <label className="text-xs font-medium text-muted-foreground">Menù</label>
                <Select value={destMenu} onValueChange={(v) => setDestMenu(v)}>
                  <SelectTrigger className="mt-1" data-testid="dish-menu-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {menus.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-muted-foreground">Categoria</label>
              <Select value={destCat} onValueChange={(v) => setDestCat(v)}>
                <SelectTrigger className="mt-1" data-testid="dish-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {catOptions.map((c) => <SelectItem key={c.id} value={c.id}>{c.parent_id ? `↳ ${c.name}` : c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {menus.length > 1 && <p className="text-[11px] text-muted-foreground">Sposta questo piatto in un'altra categoria o in un altro menù.</p>}
          </div>

          <div>
            <label className="text-sm font-medium">Formato porzione (opzionale)</label>
            <Select value={form.per_person ? "per_person" : "fixed"} onValueChange={(v) => set("per_person", v === "per_person")}>
              <SelectTrigger className="mt-1" data-testid="dish-price-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fixed">Prezzo fisso</SelectItem>
                <SelectItem value="per_person">A persona</SelectItem>
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 mt-2 cursor-pointer select-none">
              <input type="checkbox" checked={form.min_two} onChange={(e) => set("min_two", e.target.checked)} data-testid="dish-min-two" className="w-4 h-4 accent-[#002FA7]" />
              <span className="text-sm text-muted-foreground">Minimo 2 persone</span>
            </label>
            <p className="text-xs text-muted-foreground mt-1">Mostrato accanto al prezzo (es. "€ 25,00 a persona (minimo 2 persone)"). Le etichette sono tradotte automaticamente. Combinabili tra loro.</p>
          </div>

          {isDrink && (
            <div className="rounded-xl bg-muted/40 p-3 space-y-3">
              <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Dettagli bevanda</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Provenienza / Cantina</label>
                  <input value={form.origin} onChange={(e) => set("origin", e.target.value)} data-testid="dish-origin" className="mt-1 w-full rounded-xl border border-input px-3 py-2 focus:outline-none focus:border-[#002FA7]" />
                </div>
                <div>
                  <label className="text-sm font-medium">Gradazione (%)</label>
                  <input type="number" step="0.1" value={form.alcohol} onChange={(e) => set("alcohol", e.target.value)} data-testid="dish-alcohol" className="mt-1 w-full rounded-xl border border-input px-3 py-2 focus:outline-none focus:border-[#002FA7]" />
                </div>
              </div>
            </div>
          )}

          <div className="rounded-xl bg-muted/40 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Formati / varianti di prezzo (opzionale)</label>
              <button onClick={addFormat} data-testid="add-format" className="text-xs text-[#002FA7] font-semibold flex items-center gap-1"><Plus className="w-3 h-3" /> Formato</button>
            </div>
            <p className="text-xs text-muted-foreground">Per varianti con prezzi diversi (es. "Calice / Bottiglia", "per persona", "per 100g"). Compaiono come etichette sotto il piatto nel menù. Usa la × per eliminare una riga.</p>
            <div className="flex flex-wrap gap-1.5" data-testid="format-presets">
              <span className="text-[11px] text-muted-foreground w-full">Unità rapide (tap per aggiungere, poi inserisci il prezzo):</span>
              {UNIT_PRESETS.map((u, i) => (
                <button key={u} type="button" onClick={() => addPresetFormat(u)} data-testid={`format-preset-${i}`} className="rounded-full border border-[#002FA7]/40 text-[#002FA7] px-2.5 py-1 text-xs font-medium hover:bg-[#002FA7]/10 transition-colors">+ {u}</button>
              ))}
            </div>
            {form.formats.length === 0 && <p className="text-xs italic text-muted-foreground">Nessun formato.</p>}
            {form.formats.map((f, i) => (
              <div key={i} className="flex gap-2">
                <input value={f.label} onChange={(e) => updateFormat(i, "label", e.target.value)} placeholder="Es. Calice / per persona" data-testid={`format-label-${i}`} className="flex-1 rounded-lg border border-input px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7]" />
                <input type="number" step="0.01" value={f.price} onChange={(e) => updateFormat(i, "price", e.target.value)} placeholder="€" data-testid={`format-price-${i}`} className="w-24 rounded-lg border border-input px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7]" />
                <button onClick={() => removeFormat(i)} data-testid={`remove-format-${i}`} className="p-2 text-destructive"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>

          <div>
            <label className="text-sm font-medium">Allergeni</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {allergens.map((a) => {
                const Icon = allergenIcon(a.code);
                const on = form.allergens.includes(a.code);
                return (
                  <button key={a.code} type="button" onClick={() => toggleAllergen(a.code)} data-testid={`toggle-allergen-${a.code}`} className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${on ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-muted-foreground border-border hover:border-[#002FA7]"}`}>
                    <Icon className="w-3.5 h-3.5" /> {a.name_it}
                  </button>
                );
              })}
            </div>
          </div>

          {dish && activeLangs.length > 0 && (
            <div className="rounded-xl border border-border p-3 space-y-3" data-testid="dish-translations">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground flex items-center gap-1.5"><Languages className="w-4 h-4" /> Traduzioni</p>
                <button type="button" onClick={() => retranslate(null)} disabled={retLoading} data-testid="retranslate-all-btn" className="text-xs text-[#002FA7] font-semibold flex items-center gap-1 disabled:opacity-50">
                  {retLoading === "all" ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Ri-traduci tutte
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {activeLangs.map((l) => {
                  const stale = trans[l]?.stale;
                  return (
                    <button key={l} type="button" onClick={() => setSelLang(l)} data-testid={`trans-tab-${l}`} className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${selLang === l ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-muted-foreground border-border hover:border-[#002FA7]"}`}>
                      {LANG_LABELS[l] || l}{stale && <span className="ml-1 text-amber-500">•</span>}
                    </button>
                  );
                })}
              </div>
              {selLang && (
                <div className="space-y-2" data-testid={`trans-panel-${selLang}`}>
                  <input value={trans[selLang]?.name || ""} onChange={(e) => updateTrans(selLang, "name", e.target.value)} placeholder={`Nome (${LANG_LABELS[selLang] || selLang})`} data-testid={`trans-name-${selLang}`} className="w-full rounded-lg border border-input px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7]" />
                  <textarea value={trans[selLang]?.description || ""} onChange={(e) => updateTrans(selLang, "description", e.target.value)} rows={2} placeholder={`Descrizione (${LANG_LABELS[selLang] || selLang})`} data-testid={`trans-desc-${selLang}`} className="w-full rounded-lg border border-input px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7] resize-none" />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{trans[selLang]?.stale ? "Traduzione da aggiornare" : (trans[selLang]?.ai_generated ? "Generata da AI" : (trans[selLang]?.name ? "Modificata manualmente" : "Non ancora tradotta"))}</span>
                    <button type="button" onClick={() => retranslate(selLang)} disabled={retLoading} data-testid={`retranslate-${selLang}`} className="text-xs text-[#002FA7] font-semibold flex items-center gap-1 disabled:opacity-50">
                      {retLoading === selLang ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Ri-traduci questa lingua
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter className="px-6 py-4 border-t shrink-0">
          <button onClick={onClose} className="rounded-full px-5 py-2 text-sm font-medium hover:bg-muted">Annulla</button>
          <button onClick={save} disabled={saving} data-testid="dish-save-btn" className="rounded-full bg-[#002FA7] text-white px-5 py-2 text-sm font-semibold hover:bg-[#00248a] disabled:opacity-60">Salva</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DishDialog;
